/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import {
	ISchemaOptionalGroupModel,
	SCHEMA_VALUE_TYPE,
	TGenericBlueprintSchema
} from "@adapptio/lib-blueprint";
import { FieldSectionContent } from "@adapptio/lib-editor-ui";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { InspectModel } from "../InspectModel";
import { getInspectorControls } from "./inspectorControls";
import { FieldHeaderButton } from "../../Fields/FieldHeaderButton";
import { InspectorControl } from "../InspectorControl";

interface IInspectOptGroup_EditorMetaData {
	showExpanded?: boolean;
}

export const ControlOptGroup_Control: TInspectorControlComponent<
	ISchemaOptionalGroupModel<TGenericBlueprintSchema>
> = ({ model: _model, path }) => {
	const { model } = useBlueprintModel(_model, "optGroup");
	const { model: enabledModel } = useBlueprintModel(_model?.enabled);

	const meta = model ? model.schema.getEditorMetaData<IInspectOptGroup_EditorMetaData>(model) : null;

	if (!model) {
		return null;
	}

	const enabledControls = getInspectorControls(model.enabled.constant);

	return enabledControls?.primary &&
		enabledModel.type === SCHEMA_VALUE_TYPE.CONST &&
		!meta?.showExpanded ? (
		<InspectorControl config={enabledControls.primary} path={path.concat([ "enabled" ])} depth={0} />
	) : null;
};

export const ControlOptGroup_HeaderControls: TInspectorControlComponent<
	ISchemaOptionalGroupModel<TGenericBlueprintSchema>
> = ({ model: _model }) => {
	const { model } = useBlueprintModel(_model, "optGroup");
	const { model: enabledModel } = useBlueprintModel(_model?.enabled);

	const meta = model ? model.schema.getEditorMetaData<IInspectOptGroup_EditorMetaData>(model) : null;

	const toggleShowExpanded = useCallback(() => {
		if (!model) {
			return;
		}

		model.schema.setEditorMetaData<IInspectOptGroup_EditorMetaData>(model, {
			showExpanded: !meta?.showExpanded
		});
	}, [ model, meta ]);

	if (!model) {
		return null;
	}

	return (
		<FieldHeaderButton
			icon="mdi/code-braces"
			isActive={meta?.showExpanded}
			disabled={enabledModel.type !== SCHEMA_VALUE_TYPE.CONST}
			onClick={toggleShowExpanded}
		/>
	);
};

export const ControlOptGroup_Children: TInspectorControlComponent<
	ISchemaOptionalGroupModel<TGenericBlueprintSchema>
> = ({ model: _model, path, depth }) => {
	const { model } = useBlueprintModel(_model, "optGroup");
	const { model: enabledModel } = useBlueprintModel(_model?.enabled);
	const { model: enabledConstModel } = useBlueprintModel(_model?.enabled?.constant);
	const { model: valueModel } = useBlueprintModel(_model?.value);

	const meta = model ? model.schema.getEditorMetaData<IInspectOptGroup_EditorMetaData>(model) : null;

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	const showEnabled = enabledModel.type !== SCHEMA_VALUE_TYPE.CONST || meta?.showExpanded;
	const showValue =
		(enabledModel.type === SCHEMA_VALUE_TYPE.CONST && enabledConstModel?.value) ||
		enabledModel.type !== SCHEMA_VALUE_TYPE.CONST;

	return showEnabled || showValue ? (
		<FieldSectionContent>
			{showEnabled ? (
				<InspectModel model={enabledModel} path={path.concat([ "enabled" ])} depth={depth + 1} />
			) : null}
			{showValue ? (
				<InspectModel model={valueModel} path={path.concat([ "value" ])} depth={depth + 1} />
			) : null}
		</FieldSectionContent>
	) : null;
};

export const ControlOptGroup = (
	model: ISchemaOptionalGroupModel<TGenericBlueprintSchema>
): IInspectorControls => {
	return {
		primary: {
			component: ControlOptGroup_Control,
			model: model
		},
		headerControls: {
			component: ControlOptGroup_HeaderControls,
			model: model
		},
		children: {
			component: ControlOptGroup_Children,
			model: model
		}
	};
};
